import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Film, Heart, Check } from 'lucide-react';

interface PathOption {
  id: 'genre' | 'mood';
  name: string;
  description: string;
  icon: React.ReactNode;
  steps: string[];
  accent: 'gold' | 'dusk';
}

const paths: PathOption[] = [
  {
    id: 'genre',
    name: 'Genre-Based Discovery',
    description: 'Find movies by selecting your favorite genres and subgenres',
    icon: <Film className="h-7 w-7" />,
    steps: [
      'Select Genre(s)',
      'Choose Subgenres',
      'Pick Movies You Like',
      'Set Preferences',
      'Get Recommendations'
    ],
    accent: 'gold',
  },
  {
    id: 'mood',
    name: 'Mood-Based Discovery',
    description: 'Find movies that match your current mood and emotional preferences',
    icon: <Heart className="h-7 w-7" />,
    steps: [
      'Select Mood(s)',
      'Pick Movies You Like',
      'Set Preferences',
      'Get Recommendations'
    ],
    accent: 'dusk',
  }
];

interface Props {
  onSelect: (path: 'genre' | 'mood') => void;
}

export function PathSelection({ onSelect }: Props) {
  const [selectedPath, setSelectedPath] = useState<'genre' | 'mood' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePathSelect = (pathId: 'genre' | 'mood') => {
    setSelectedPath(pathId);
    setError(null);
  };

  const handleSubmit = () => {
    if (!selectedPath) {
      setError('Please select a discovery path');
      return;
    }
    onSelect(selectedPath);
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2 text-center">
        <h2 className="display text-2xl font-semibold sm:text-3xl">
          How do you want to find your <span className="text-gradient-dusk">movie</span>?
        </h2>
        <p className="text-muted-foreground">
          Pick a path — both end with recommendations made just for you.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {paths.map((path) => {
          const isSelected = selectedPath === path.id;
          const isGold = path.accent === 'gold';

          return (
            <button
              key={path.id}
              onClick={() => handlePathSelect(path.id)}
              className={cn(
                'select-card w-full p-6',
                isSelected && (isGold
                  ? 'select-card-active'
                  : 'border-accent/70 bg-accent/[0.08] shadow-glow-dusk hover:border-accent hover:bg-accent/[0.1]')
              )}
              aria-pressed={isSelected}
            >
              {isSelected && (
                <span
                  className={cn(
                    'absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full',
                    isGold ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground'
                  )}
                >
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
              )}
              <div className="space-y-4">
                <div
                  className={cn(
                    'inline-flex rounded-2xl border p-3.5 transition-colors duration-200',
                    isGold
                      ? 'border-primary/25 bg-primary/10 text-primary'
                      : 'border-accent/25 bg-accent/10 text-accent'
                  )}
                >
                  {path.icon}
                </div>
                <div>
                  <h3 className="mb-1.5 text-lg font-semibold text-foreground">
                    {path.name}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {path.description}
                  </p>
                </div>
                <ol className="space-y-1.5 border-t border-white/[0.06] pt-4">
                  {path.steps.map((step, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-2.5 text-sm text-muted-foreground"
                    >
                      <span
                        className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                          isGold ? 'bg-primary/15 text-primary' : 'bg-accent/15 text-accent'
                        )}
                      >
                        {index + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="text-center text-sm text-red-400">{error}</p>
      )}

      <button onClick={handleSubmit} className="btn-marquee w-full px-4 py-3.5 text-base">
        Continue
      </button>
    </div>
  );
}
